import { PlayerStats } from './constants';

export interface AnalysisData {
  grade: string;
  totalScore: number;
  reactionSpeed: number;
  bodyCoordination: number;
  combatEfficiency: number;
  survival: number;
  attentionIndex: number;
  aiComment: string;
}

export function calculateGrade(score: number): string {
  if (score >= 95) return 'S+';
  if (score >= 85) return 'S';
  if (score >= 75) return 'A';
  if (score >= 60) return 'B';
  if (score >= 40) return 'C';
  return 'D';
}

export function calculateAnalysis(
  playerStats: PlayerStats,
  challengeStats: { total: number; success: number; fail: number; rate: number },
  hitRate: number,
  battleTime: number,
  won: boolean
): AnalysisData {
  // Reaction speed: based on challenge completion rate and speed
  const reactionSpeed = Math.min(100, Math.round(
    challengeStats.rate * 60 + (challengeStats.total > 0 ? Math.min(40, challengeStats.success * 5) : 20)
  ));

  // Body coordination: based on head tracking accuracy and dodge success
  const bodyMoves = playerStats.headMovements;
  const dodges = playerStats.dodges;
  const bodyCoordination = Math.min(100, Math.round(
    Math.min(60, bodyMoves * 2) + Math.min(40, dodges * 8)
  ));

  // Combat efficiency: based on hit rate, skill usage, combo
  const combatEfficiency = Math.min(100, Math.round(
    hitRate * 50 + Math.min(25, playerStats.maxCombo * 3) + Math.min(25, playerStats.skillUses * 5)
  ));

  // Survival: based on damage taken vs battle time
  const survivalBase = Math.max(0, 100 - playerStats.damagesTaken * 8);
  const survival = Math.min(100, Math.round(survivalBase + (won ? 20 : 0)));

  // Attention index: challenge completion + multitasking
  const attentionIndex = Math.min(100, Math.round(
    challengeStats.rate * 50 + Math.min(30, challengeStats.total * 3) + (playerStats.shieldUses > 0 ? 20 : 0)
  ));

  // Total score
  const totalScore = Math.round(
    reactionSpeed * 0.2 +
    bodyCoordination * 0.15 +
    combatEfficiency * 0.3 +
    survival * 0.2 +
    attentionIndex * 0.15
  );

  const grade = calculateGrade(totalScore);

  // AI comment generation
  const aiComment = generateAIComment(reactionSpeed, bodyCoordination, combatEfficiency, survival, attentionIndex, won);

  return {
    grade,
    totalScore,
    reactionSpeed,
    bodyCoordination,
    combatEfficiency,
    survival,
    attentionIndex,
    aiComment,
  };
}

function generateAIComment(
  reaction: number,
  coordination: number,
  efficiency: number,
  survival: number,
  attention: number,
  won: boolean
): string {
  const comments: string[] = [];

  if (won) {
    comments.push('战斗胜利！表现出色。');
  } else {
    comments.push('战斗失败。不要气馁，继续挑战。');
  }

  if (reaction >= 80) {
    comments.push('你的反应速度优秀，面对挑战时能快速做出正确判断。');
  } else if (reaction >= 50) {
    comments.push('反应速度良好，但面对多重挑战时可以进一步提升响应速度。');
  } else {
    comments.push('建议多关注屏幕四周的挑战事件，提高注意力分配能力。');
  }

  if (coordination >= 70) {
    comments.push('身体协调能力出色，头部动作控制精准。');
  } else {
    comments.push('身体协调仍有提升空间，建议更流畅地配合头部动作与键盘操作。');
  }

  if (efficiency >= 75) {
    comments.push('战斗效率很高，攻击命中率和技能利用率都很优秀。');
  } else if (efficiency >= 50) {
    comments.push('攻击节奏可以更加合理，建议更多利用护盾和闪避来创造输出机会。');
  } else {
    comments.push('建议优化攻击节奏，结合护盾防御后再进行反击。');
  }

  if (survival < 50) {
    comments.push('生存能力较弱，建议多利用闪避和护盾减少受伤。');
  }

  if (attention >= 70) {
    comments.push('注意力分配能力出色，能同时应对Boss和挑战事件。');
  }

  return comments.join(' ');
}
